import React, {
    useEffect,
    useState,
    useRef,
    useMemo,
    useCallback,
  } from "react";
  import {
    Box,
    IconButton,
    Typography,
    useTheme,
    TextField,
    Autocomplete,
  } from "@mui/material";
  import Tabs, { tabsClasses } from "@mui/material/Tabs";
  import Table from "@mui/material/Table";
  import TableBody from "@mui/material/TableBody";
  import TableCell from "@mui/material/TableCell";
  import TableRow from "@mui/material/TableRow";
  import { Card, TableContainer, Button, TableHead } from "@mui/material";
  import { styled } from "@mui/material";
  import {
    requestMarketData,
    setCalculatedMonitorScreen,
  } from "../../Redux/Reducers/marketDataSlice";
  import {
    TABLE_HEAD_VANTAGE as TABLE_HEAD,
    applySortFilter,
    calculateAndFormat,
    getComparator,
    StyledTab,
    calculateAndFormatNoAbs,
  } from "../../component/Methods";
  import FullscreenIcon from "@mui/icons-material/Fullscreen";
  import { useNavigate } from "react-router-dom";
  import { useDispatch, useSelector } from "react-redux";
  import MonitorScreenCalculation from "../Portfolio/MonitorScreenCalculation";
  import VantageListHead from "../../component/ListHead/VantageListHead";
  import PerformanceFundPopUp from "./Popup/PerformanceFundPopUp";
  import FundStrategyPopUp from "./Popup/FundStrategyPopUp";
  import { StrategiesDashboard } from "../../component/Methods/dashboardCalculations";
  import PositionPeformance from "./Popup/PositionPerformance";
  import BenchMarkTable from "./BenchMarkTable";
  import Switch from "@mui/material/Switch";
  import Loader from "../../component/Loader";
  import { getMarketDataThreshold } from "../../Redux/Reducers/selectors";
  import { useVantageSystemState } from "../../hooks/useOptimizedState";
  
  const label = { inputProps: { "aria-label": "Switch demo" } };
  
  const Tablecell = styled(TableCell)(({ theme }) => ({
    fontSize: "12px",
    padding: "1px 2px 1px 5px",
  }));
  
  const PerformanceDetails= () => {
    const threshold = useSelector(getMarketDataThreshold);
    const dispatch = useDispatch();
    const navigate = useNavigate();
  
    const [monitorScreenValuesObj, setMonitorScreenValuesObj] = useState([]);
    const [order, setOrder] = useState("desc");
    const [orderBy, setOrderBy] = useState("aum");
    const [filterName, setFilterName] = useState("");
    const [monitor, setMonitor] = useState([]);
    const [isGridViewPositions, setIsGridViewPositions] = useState(false);
    const [isGridViewPerformance, setIsGridViewPerformance] = useState(false);
    const [isPositionPerformance, setIsPositionPerformance] = useState(false);
    const [checked, setChecked] = useState(true);
    const [value, setValue] = React.useState(0);
    const [isMarketData, setIsMarketData] = useState(false);
    const [dataFetching, setDataFetching] = useState(false);
    const [selectedFund, setSelectedFund] = useState(null);
  
    // Keep the optimized Redux hook - this is the most important optimization
    const {
      funds,
      strategies,
      trades,
      lots,
      livePrices,
      markets,
      marketData,
      RawMarketData,
      accountingData,
      isStrategist,
    } = useVantageSystemState();
  
    // Simple calculation - no memoization needed for simple functions
    const resultArray = StrategiesDashboard();
    useEffect(() => {
      const filteredData = MonitorScreenCalculation({
        selectedFund: undefined,
        selectedStrategy: undefined,
        selectedSide: undefined,
        lots: lots,
        accountingData: accountingData,
        funds: funds,
        strategies: strategies,
        marketData: livePrices,
        trades: trades,
        isStrategist: isStrategist,
        markets: markets,
      });
      if (filteredData) dispatch(setCalculatedMonitorScreen(filteredData));
    }, [
      lots,
      accountingData,
      funds,
      strategies,
      livePrices,
      trades,
      isStrategist,
      markets,
    ]);
  
    const timeoutRef = useRef(null);
    const [isFirstRender, setIsFirstRender] = useState(true);
  
    useEffect(() => {
      if (isFirstRender) {
        setIsFirstRender(false);
        return;
      }
  
      // Clear the previous timeout if it exists
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
  
      setDataFetching(true);
      // Set a new timeout to trigger the function after 20 seconds
      timeoutRef.current = setTimeout(() => {
        setIsMarketData(false);
        dispatch(
          requestMarketData({
            lots: lots,
            threshold: threshold,
            noRequest: true,
            trades: trades,
          })
        );
      }, 20000); // 10 seconds
  
      // Cleanup the timeout if the component unmounts or RawMarketData changes
      return () => clearTimeout(timeoutRef.current);
    }, [RawMarketData]);
  
    const handleChange = (event, newValue) => {
      setValue(newValue);
    };
    const handleChangeCheck = (event) => {
      setChecked(event.target.checked);
    };
  
    // Memoize expensive calculation - this is safe and provides good performance benefit
    const calculatedValues = useMemo(
      () =>
        MonitorScreenCalculation({
          lots: lots,
          accountingData: accountingData,
          funds: funds,
          strategies: strategies,
          marketData: livePrices,
          markets: markets,
        }),
      [lots, accountingData, funds, strategies, livePrices, markets]
    );
    const findFundAum = (key, array) => {
      if (key && array && array[0]?.funds) {
        const findAum = array[0]?.funds?.find((i) => i.fund === key);
        return findAum?.aum;
      }
    };
  
    // Memoize expensive accFund calculation
    const accFund = useMemo(() => {
      if (!funds) return [];
  
      const findFunds = funds?.filter(
        (i) =>
          i.abbreviation === "wsf" ||
          i.abbreviation === "pmp" ||
          i.abbreviation === "top" ||
          i.abbreviation === "dbbs" ||
          i.abbreviation === "ral1" ||
          i.abbreviation === "ral2" ||
          i.abbreviation === "jk" ||
          i.abbreviation === "sthw" ||
          i.abbreviation === "sche" ||
          i.abbreviation === "spir"
      );
  
      const transformedFunds = findFunds?.map((fund) => {
        const calc = MonitorScreenCalculation({
          selectedFund: fund.fundId,
          lots: lots,
          accountingData: accountingData,
          funds: funds,
          strategies: strategies,
          marketData: livePrices,
          markets: markets,
        });
  
        let totalPL = calc.reduce(
          (sum, currentObj) => sum + parseFloat(currentObj.pL),
          0
        );
  
        return {
          name: fund.name,
          aum: findFundAum(fund.fundId, accountingData),
          size: totalPL,
          pf:
            (totalPL / parseFloat(findFundAum(fund.fundId, accountingData))) *
            100,
        };
      });
  
      const totalAum = transformedFunds.reduce((total, fund) => {
        return fund.aum ? total + fund.aum : total;
      }, 0);
      const size = transformedFunds.reduce((total, fund) => {
        return fund.size ? total + fund.size : total;
      }, 0);
      const pf = transformedFunds.reduce((total, fund) => {
        return fund.pf ? total + fund.pf : total;
      }, 0);
  
      transformedFunds.push({
        name: "Totals",
        aum: totalAum,
        size: size,
        pf: pf,
      });
  
      return transformedFunds;
    }, [
      funds,
      accountingData,
      marketData,
      lots,
      strategies,
      livePrices,
      markets,
    ]);
  
    // Memoize filtered table data
    const filteredTableData = useMemo(() => {
      return monitorScreenValuesObj?.filter((row) => {
        const meetsAumCondition =
          (checked && parseFloat(row?.aum) > 10) || !checked;
        const meetsFundCondition =
          !selectedFund || row.fundName === selectedFund.label;
        return meetsAumCondition && meetsFundCondition;
      });
    }, [monitorScreenValuesObj, checked, selectedFund]);
  
    // Memoize fund names for autocomplete
    const getUniqueFundNames = useMemo(() => {
      const fundNames = new Set();
      monitorScreenValuesObj?.forEach((row) => {
        if (row.fundName) {
          fundNames.add(row.fundName);
        }
      });
      return Array.from(fundNames).map((name) => ({ label: name }));
    }, [monitorScreenValuesObj]);
  
    // Memoize static array
    const positions = useMemo(() => ["Fund", "AUM", "P(L)", "P(L)%"], []);
  
    // Optimize event handlers with useCallback
    const handleCellClick = useCallback(
      (fundName) => {
        navigate(`/portfolio/${fundName}`);
      },
      [navigate]
    );
  
    const handleCellClickStrategy = useCallback(
      (fundName, strategyName) => {
        navigate(`/portfolio/${fundName}/${strategyName}`);
      },
      [navigate]
    );
  
    const handleRequestSort = useCallback(
      (event, property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
      },
      [orderBy, order]
    );
  
    const toggleGridViewPositions = useCallback(() => {
      setIsGridViewPositions((prev) => !prev);
    }, []);
  
    const toggleGridViewPerformance = useCallback(() => {
      setIsGridViewPerformance((prev) => !prev);
    }, []);
  
    const handleMarketData = useCallback(() => {
      setIsMarketData(true);
      setDataFetching(false);
  
      dispatch(
        requestMarketData({ lots: lots, threshold: threshold, trades: trades })
      );
  
      setTimeout(() => {
        if (!dataFetching) {
          setIsMarketData(false);
        }
      }, 20000);
    }, [dispatch, lots, threshold, trades, dataFetching]);
    useEffect(() => {
      setMonitor(resultArray);
      const filteredUsers = applySortFilter(
        resultArray,
        getComparator(order, orderBy),
        filterName
      );
      setMonitorScreenValuesObj(filteredUsers);
    }, [order, orderBy, filterName, calculatedValues]);
  
    // Utility function
  
    const theme = useTheme();
  
    return isMarketData ? (
      <Loader msg={true} marketLoader={true} />
    ) : (
      <>
        <Box
          sx={{
            // Adjust this value based on header height + padding
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 2,
              mb: 0,
            }}
          >
            <Box sx={{ width: "50%", marginRight: 0, padding: "0px" }}>
              {" "}
              <Box
                sx={{
                  border: `1px solid ${theme.palette.lightGrey.tableHeader}`,
                  borderRadius: "4px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    background: theme.palette.lightGrey.tableHeader,
                    px: 1,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ color: theme.palette.lightGrey.textColor }}
                  >
                    Longs and Shorts Summary
                  </Typography>
                  <IconButton
                    onClick={toggleGridViewPositions}
                    aria-label="Toggle GridView"
                    sx={{ height: "20px", width: "20px" }}
                  >
                    <FullscreenIcon sx={{ height: "15px", width: "15px" }} />
                  </IconButton>
                </Box>
                <Table aria-label="a dense table" size="small">
                  <TableHead>
                    <TableRow>
                      {positions?.map((i, index) => {
                        return (
                          <Tablecell
                            align="center"
                            key={index}
                            sx={{
                              borderRight: `1px solid ${theme.palette.lightGrey.tableHeader}`,
                              color: theme.palette.lightGrey.textColor,
                              fontSize: theme.typography.bodyText12.fontSize,
                              fontWeight: theme.typography.bodyText12.fontWeight,
                            }}
                          >
                            {i}
                          </Tablecell>
                        );
                      })}
                    </TableRow>
                  </TableHead>
  
                  <TableBody>
                    {accFund?.map((row, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          background: theme.palette.grey[100],
                          "&:hover": {
                            background: theme.palette.grey[200],
                            cursor: "pointer",
                          },
                        }}
                      >
                        <Tablecell
                          align="left"
                          sx={{
                            borderRight: `1px solid ${theme.palette.lightGrey.tableHeader}`,
                          }}
                        >
                          {row?.name}
                        </Tablecell>
                        <Tablecell
                          sx={{
                            borderRight: `1px solid ${theme.palette.lightGrey.tableHeader}`,
                          }}
                          align="right"
                        >
                          {!isNaN(row?.aum) &&
                          row?.aum !== null &&
                          row?.aum !== undefined
                            ? calculateAndFormat(row?.aum)
                            : ""}
                        </Tablecell>
                        <Tablecell
                          style={{
                            borderRight: `1px solid ${theme.palette.lightGrey.tableHeader}`,
                            color:
                              row?.size >= 0
                                ? theme.palette.mode === "dark"
                                  ? "#4ade80"
                                  : "#16a34a"
                                : theme.palette.mode === "dark"
                                ? "#f87171"
                                : "#dc2626",
                          }}
                          align="right"
                        >
                          {!isNaN(row?.size) &&
                          row?.size !== null &&
                          row?.size !== undefined ? (
                            row?.size >= 0 ? (
                              <>{calculateAndFormat(row?.size)}&nbsp;</>
                            ) : (
                              "(" + calculateAndFormat(row?.size * -1) + ")"
                            )
                          ) : (
                            ""
                          )}
                        </Tablecell>
                        <Tablecell
                          style={{
                            borderRight: `1px solid ${theme.palette.lightGrey.tableHeader}`,
                            color:
                              row?.pf >= 0
                                ? theme.palette.mode === "dark"
                                  ? "#4ade80"
                                  : "#16a34a"
                                : theme.palette.mode === "dark"
                                ? "#f87171"
                                : "#dc2626",
                          }}
                          align="right"
                        >
                          {!isNaN(row?.pf) &&
                          row?.pf !== null &&
                          row?.pf !== undefined ? (
                            row?.pf >= 0 ? (
                              <>{row?.pf.toFixed(2) + "%"}&nbsp;</>
                            ) : (
                              "(" + (row?.pf * -1).toFixed(2) + "%)"
                            )
                          ) : (
                            ""
                          )}
                        </Tablecell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
              <Tabs
                value={value}
                onChange={handleChange}
                allowScrollButtonsMobile
                aria-label="scrollable force tabs example"
                sx={{
                  [`& .${tabsClasses.scrollButtons}`]: {
                    "&.Mui-disabled": { opacity: 0.3 },
                    borderRadius: "50%",
                    background: "#808080",
                    height: "20px",
                    width: "20px",
                    mt: 2,
                  },
                }}
                indicatorColor="white"
                textColor="inherit"
              >
                {/* <StyledTab
                    onClick={() => {
                      setIsPositionPerformance(true);
                    }}
                    label="Positions Performance"
                  /> */}
              </Tabs>
            </Box>
            <Box sx={{ width: "50%", marginRight: 0, padding: "0px" }}>
              <BenchMarkTable marketData={livePrices} />
            </Box>
          </Box>
  
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              mt: -2,
            }}
          >
            <Box sx={{ mb: 2, flexShrink: 0 }}>
              <Autocomplete
                size="small"
                options={getUniqueFundNames}
                value={selectedFund}
                onChange={(event, newValue) => {
                  setSelectedFund(newValue);
                }}
                sx={{ width: 200 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter by Fund"
                    variant="outlined"
                    size="small"
                  />
                )}
              />
            </Box>
  
            <Box
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                overflow: "hidden",
                flex: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <TableContainer sx={{ flex: 1, overflow: "auto" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    background: theme.palette.lightGrey.tableHeader,
                    px: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ color: theme.palette.lightGrey.textColor }}
                    >
                      Funds and Strategies Performance
                    </Typography>
                  </Box>
  
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ color: theme.palette.lightGrey.textColor }}
                    >
                      Hide strategies with zero AUM
                    </Typography>
                    <Switch
                      size="small"
                      checked={checked}
                      onChange={handleChangeCheck}
                      inputProps={{ "aria-label": "controlled" }}
                    />
  
                    <IconButton
                      onClick={toggleGridViewPerformance}
                      aria-label="Toggle GridView"
                      sx={{ height: "20px", width: "20px" }}
                    >
                      <FullscreenIcon sx={{ height: "15px", width: "15px" }} />
                    </IconButton>
                  </Box>
                </Box>
  
                <Table
                  size="small"
                  stickyHeader
                  aria-label="a dense table"
                  sx={{
                    height: "max-content",
                    borderCllapse: "collapse",
                    border: "solid 1px #eee",
                  }}
                >
                  <VantageListHead
                    order={order}
                    orderBy={orderBy}
                    headLabel={TABLE_HEAD}
                    noStatus={true}
                    onRequestSort={handleRequestSort}
                  />
  
                  <TableBody>
                    {filteredTableData?.map((row, i) => (
                      <TableRow rowheight={0} key={i}>
                        <Tablecell
                          align="left"
                          onClick={() => handleCellClick(row.fundName)}
                          sx={{
                            cursor: "pointer",
                            fontWeight: "500",
                            paddingLeft: "5px",
                          }}
                        >
                          {row.fundName}
                        </Tablecell>
                        <Tablecell
                          align="left"
                          onClick={() =>
                            row.strategyName == "CPFactorBW"
                              ? handleCellClick(row.fundName)
                              : handleCellClickStrategy(
                                  row.fundName,
                                  row.strategyName
                                )
                          }
                          sx={{
                            cursor: "pointer",
                            paddingLeft: "5px",
                            width: "10",
                          }}
                        >
                          {row.strategyName}
                        </Tablecell>
                        <Tablecell align="right" sx={{ paddingRight: "5px" }}>
                          {!isNaN(row?.aum) &&
                          row?.aum !== null &&
                          row?.aum !== undefined ? (
                            row?.aum >= 0 ? (
                              <>{calculateAndFormat(row?.aum)}&nbsp;</>
                            ) : (
                              `(${calculateAndFormat(row?.aum * -1)})`
                            )
                          ) : (
                            ""
                          )}
                        </Tablecell>
                        <Tablecell
                          align="right"
                          style={{
                            paddingRight: "5px",
                            color:
                              row?.pL >= 0
                                ? theme.palette.mode === "dark"
                                  ? "#4ade80"
                                  : "#16a34a"
                                : theme.palette.mode === "dark"
                                ? "#f87171"
                                : "#dc2626",
                          }}
                        >
                          {!isNaN(row?.pL) &&
                          row?.pL !== null &&
                          row?.pL !== undefined ? (
                            row?.pL >= 0 ? (
                              <>{calculateAndFormat(row?.pL)}&nbsp;</>
                            ) : (
                              `(${calculateAndFormat(row?.pL * -1)})`
                            )
                          ) : (
                            ""
                          )}
                        </Tablecell>
                        <Tablecell
                          align="right"
                          style={{
                            paddingRight: "5px",
  
                            color:
                              row.plLong >= 0
                                ? theme.palette.mode === "dark"
                                  ? "#4ade80"
                                  : "#16a34a"
                                : theme.palette.mode === "dark"
                                ? "#f87171"
                                : "#dc2626",
                          }}
                        >
                          {!isNaN(row?.plLong) &&
                          row?.plLong !== null &&
                          row?.plLong !== undefined ? (
                            row?.plLong >= 0 ? (
                              <>{calculateAndFormat(row?.plLong)}&nbsp;</>
                            ) : (
                              `(${calculateAndFormat(row?.plLong * -1)})`
                            )
                          ) : (
                            ""
                          )}
                        </Tablecell>
                        <Tablecell
                          align="right"
                          style={{
                            paddingRight: "5px",
  
                            color:
                              row?.plShort >= 0
                                ? theme.palette.mode === "dark"
                                  ? "#4ade80"
                                  : "#16a34a"
                                : theme.palette.mode === "dark"
                                ? "#f87171"
                                : "#dc2626",
                          }}
                        >
                          {!isNaN(row?.plShort) &&
                          row?.plShort !== null &&
                          row?.plShort !== undefined ? (
                            row?.plShort >= 0 ? (
                              <>{calculateAndFormat(row?.plShort)}&nbsp;</>
                            ) : (
                              `(${calculateAndFormat(row?.plShort * -1)})`
                            )
                          ) : (
                            ""
                          )}
                        </Tablecell>
                        <Tablecell align="right" sx={{ paddingRight: "5px" }}>
                          {!isNaN(parseFloat(row?.sizeLong)) &&
                          row?.sizeLong !== null &&
                          row?.sizeLong !== undefined
                            ? parseFloat(row?.sizeLong).toFixed(2) + "%"
                            : ""}
                        </Tablecell>
                        <Tablecell align="right" sx={{ paddingRight: "5px" }}>
                          {!isNaN(row?.sizeShort) &&
                          row?.sizeShort !== null &&
                          row?.sizeShort !== undefined
                            ? row?.sizeShort < 0
                              ? `(${Math.abs(row?.sizeShort).toFixed(2)}%)`
                              : `${row?.sizeShort.toFixed(2)}%`
                            : ""}
                        </Tablecell>
                        <Tablecell align="right" sx={{ paddingRight: "5px" }}>
                          {!isNaN(row?.sizeLong) &&
                          !isNaN(row?.sizeShort) &&
                          row?.sizeLong !== null &&
                          row?.sizeShort !== null
                            ? (() => {
                                const netAumPercent =
                                  parseFloat(row.sizeLong || 0) +
                                  parseFloat(row.sizeShort || 0);
                                return netAumPercent >= 0
                                  ? `${netAumPercent.toFixed(2)}%`
                                  : `(${Math.abs(netAumPercent).toFixed(2)}%)`;
                              })()
                            : ""}
                        </Tablecell>
                        <Tablecell align="right" sx={{ paddingRight: "5px" }}>
                          {!isNaN(row?.longCapital) &&
                          row?.longCapital !== null &&
                          row?.longCapital !== undefined ? (
                            row?.longCapital >= 0 ? (
                              <>
                                {calculateAndFormatNoAbs(row?.longCapital)}
                                &nbsp;
                              </>
                            ) : (
                              `(${calculateAndFormat(row?.longCapital * -1)})`
                            )
                          ) : (
                            ""
                          )}
                        </Tablecell>
                        <Tablecell align="right" sx={{ paddingRight: "5px" }}>
                          {!isNaN(row?.shortCapital) &&
                          row?.shortCapital !== null &&
                          row?.shortCapital !== undefined ? (
                            row?.shortCapital ? (
                              <>
                                {calculateAndFormatNoAbs(row?.shortCapital * -1)}
                                &nbsp;
                              </>
                            ) : (
                              0
                            )
                          ) : (
                            ""
                          )}
                        </Tablecell>
                        <Tablecell align="right" sx={{ paddingRight: "5px" }}>
                          {!isNaN(row?.longCapital) &&
                          !isNaN(row?.shortCapital) &&
                          row?.longCapital !== null &&
                          row?.shortCapital !== null
                            ? (() => {
                                const netCapital =
                                  parseFloat(row.longCapital || 0) +
                                  parseFloat(row.shortCapital || 0);
                                return netCapital >= 0 ? (
                                  <>{calculateAndFormat(netCapital)}&nbsp;</>
                                ) : (
                                  "(" + calculateAndFormat(netCapital * -1) + ")"
                                );
                              })()
                            : ""}
                        </Tablecell>
                        <Tablecell
                          align="right"
                          style={{
                            paddingRight: "5px",
                            color:
                              row?.longPerformance >= 0
                                ? theme.palette.mode === "dark"
                                  ? "#4ade80"
                                  : "#16a34a"
                                : theme.palette.mode === "dark"
                                ? "#f87171"
                                : "#dc2626",
                          }}
                        >
                          {!isNaN(row?.longPerformance) &&
                          row?.longPerformance !== null &&
                          row?.longPerformance !== undefined ? (
                            row?.longPerformance >= 0 ? (
                              <>{row?.longPerformance.toFixed(2)}%&nbsp;</>
                            ) : (
                              <>({(row?.longPerformance * -1).toFixed(2)}%)</>
                            )
                          ) : (
                            ""
                          )}
                        </Tablecell>
                        <Tablecell
                          align="right"
                          style={{
                            paddingRight: "5px",
                            color:
                              row?.shortPerformance > 0
                                ? theme.palette.mode === "dark"
                                  ? "#f87171"
                                  : "#dc2626"
                                : theme.palette.mode === "dark"
                                ? "#4ade80"
                                : "#16a34a",
                          }}
                        >
                          {!isNaN(row?.shortPerformance) &&
                          row?.shortPerformance !== null &&
                          row?.shortPerformance !== undefined ? (
                            row?.shortPerformance >= 0 ? (
                              <>{row?.shortPerformance.toFixed(2)}%&nbsp;</>
                            ) : (
                              <>({(row?.shortPerformance * -1).toFixed(2)}%)</>
                            )
                          ) : (
                            ""
                          )}
                        </Tablecell>
                        <Tablecell
                          align="right"
                          style={{
                            paddingRight: "5px",
                            color:
                              row?.plPer >= 0
                                ? theme.palette.mode === "dark"
                                  ? "#4ade80"
                                  : "#16a34a"
                                : theme.palette.mode === "dark"
                                ? "#f87171"
                                : "#dc2626",
                          }}
                        >
                          {!isNaN(row?.plPer) &&
                          row?.plPer !== null &&
                          row?.plPer !== undefined ? (
                            row?.plPer >= 0 ? (
                              <>{(row?.plPer).toFixed(2)}%&nbsp;</>
                            ) : (
                              <>({Math.abs(row?.plPer).toFixed(2)}%)</>
                            )
                          ) : (
                            ""
                          )}
                        </Tablecell>
                        <Tablecell
                          align="right"
                          style={{
                            paddingRight: "5px",
  
                            color:
                              row?.pLWeek >= 0
                                ? theme.palette.mode === "dark"
                                  ? "#4ade80"
                                  : "#16a34a"
                                : theme.palette.mode === "dark"
                                ? "#f87171"
                                : "#dc2626",
                          }}
                        >
                          {!isNaN(row?.pLWeek) &&
                          row?.pLWeek !== null &&
                          row?.pLWeek !== undefined ? (
                            row?.pLWeek >= 0 ? (
                              <>{calculateAndFormat(row?.pLWeek)}&nbsp;</>
                            ) : (
                              `(${calculateAndFormat(row?.pLWeek * -1)})`
                            )
                          ) : (
                            ""
                          )}
                        </Tablecell>
                        <Tablecell
                          align="right"
                          style={{
                            paddingRight: "5px",
  
                            color:
                              row?.pLMonth >= 0
                                ? theme.palette.mode === "dark"
                                  ? "#4ade80"
                                  : "#16a34a"
                                : theme.palette.mode === "dark"
                                ? "#f87171"
                                : "#dc2626",
                          }}
                        >
                          {!isNaN(row?.pLMonth) &&
                          row?.pLMonth !== null &&
                          row?.pLMonth !== undefined ? (
                            row?.pLMonth >= 0 ? (
                              <>{calculateAndFormat(row?.pLMonth)}&nbsp;</>
                            ) : (
                              `(${calculateAndFormat(row?.pLMonth * -1)})`
                            )
                          ) : (
                            ""
                          )}
                        </Tablecell>
                        <Tablecell
                          align="right"
                          style={{
                            paddingRight: "5px",
  
                            color:
                              row?.pLQuarter >= 0
                                ? theme.palette.mode === "dark"
                                  ? "#4ade80"
                                  : "#16a34a"
                                : theme.palette.mode === "dark"
                                ? "#f87171"
                                : "#dc2626",
                          }}
                        >
                          {!isNaN(row?.pLQuarter) &&
                          row?.pLQuarter !== null &&
                          row?.pLQuarter !== undefined ? (
                            row?.pLQuarter >= 0 ? (
                              <>{calculateAndFormat(row?.pLQuarter)}&nbsp;</>
                            ) : (
                              `(${calculateAndFormat(row?.pLQuarter * -1)})`
                            )
                          ) : (
                            ""
                          )}
                        </Tablecell>
                        <Tablecell
                          align="right"
                          style={{
                            paddingRight: "5px",
  
                            color:
                              row?.pLYear >= 0
                                ? theme.palette.mode === "dark"
                                  ? "#4ade80"
                                  : "#16a34a"
                                : theme.palette.mode === "dark"
                                ? "#f87171"
                                : "#dc2626",
                          }}
                        >
                          {!isNaN(row?.pLYear) &&
                          row?.pLYear !== null &&
                          row?.pLYear !== undefined ? (
                            row?.pLYear >= 0 ? (
                              <>{calculateAndFormat(row?.pLYear)}&nbsp;</>
                            ) : (
                              `(${calculateAndFormat(row?.pLYear * -1)})`
                            )
                          ) : (
                            ""
                          )}
                        </Tablecell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        </Box>
  
        <PositionPeformance
          title="POSITIONS PERFORMANCE"
          isPositionPerformance={isPositionPerformance}
          setIsPositionPerformance={setIsPositionPerformance}
        />
  
        <PerformanceFundPopUp
          openPositionsPopup={isGridViewPositions}
          setopenPositionsPopup={setIsGridViewPositions}
          accFund={accFund}
          tablehead={positions}
          title="Longs and Shorts Summary"
        />
        <FundStrategyPopUp
          openPerformancePopup={isGridViewPerformance}
          setopenPerformancePopup={setIsGridViewPerformance}
          checked={checked}
          monitor={filteredTableData}
          title="Funds and Strategies Performance"
        />
      </>
    );
  };
  export default PerformanceDetails;
  